import {Component, Inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {
  NoteFormGroupFactory,
  ScheduleDetailData,
  ScheduleListData,
  ScheduleVersion,
  ScheduleVersionHelper,
  ScheduleVersionListData
} from '@api/planner';

interface DialogData {
  scheduleId: number;
  scheduleName: string;
}

@Component({
  selector: 'app-version-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  providers: [
    ScheduleVersionListData,
    ScheduleVersionHelper,
  ],
  templateUrl: './version-panel.component.html',
  styleUrls: ['./version-panel.component.scss'],
})
export class VersionPanelComponent implements OnInit {
  selectedVersionIds: Set<number> = new Set();
  editingNoteId: number | null = null;
  noteFormGroup!: FormGroup;
  revertingVersionId: number | null = null;
  deletingVersionIds: Set<number> = new Set();

  constructor(
    public dialogRef: MatDialogRef<VersionPanelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private snackBar: MatSnackBar,
    public versionListData: ScheduleVersionListData,
    public versionHelper: ScheduleVersionHelper,
    private scheduleListData: ScheduleListData,
    private scheduleDetailData: ScheduleDetailData,
    private noteFormGroupFactory: NoteFormGroupFactory,
  ) {
    this.noteFormGroup = this.noteFormGroupFactory.create();
  }

  ngOnInit() {
    this.versionListData.setScheduleId(this.data.scheduleId);
    this.versionListData.loadItems().subscribe({
      error: (err) => {
        console.error('Failed to load versions', err);
        this.snackBar.open('Failed to load versions', 'Close', {duration: 3000});
      },
    });
  }

  toggleSelection(versionId: number) {
    this.selectedVersionIds.has(versionId)
      ? this.selectedVersionIds.delete(versionId)
      : this.selectedVersionIds.add(versionId);
  }

  selectAllNonActive() {
    (this.versionListData.peekItems() ?? []).forEach(v => {
      if (!v.isActive) {
        this.selectedVersionIds.add(v.id);
      }
    });
  }

  clearSelection() {
    this.selectedVersionIds.clear();
  }

  startEditNote(version: ScheduleVersion) {
    this.editingNoteId = version.id;
    this.noteFormGroup.controls['note'].setValue(version.note || '');
  }

  cancelEditNote() {
    this.editingNoteId = null;
    this.noteFormGroup.reset();
  }

  saveNote(version: ScheduleVersion) {
    this.versionHelper.saveNote(version, this.noteFormGroup.value.note ?? '').subscribe({
      next: () => this.cancelEditNote(),
      error: (err) => {
        console.error('Failed to save note', err);
        this.snackBar.open('Failed to save note', 'Close', { duration: 3000 });
      },
    });
  }

  revertToVersion(version: ScheduleVersion) {
    this.versionHelper.revertWithDialog(this.data.scheduleId, version, {
      onStart: () => this.revertingVersionId = version.id,
      onSuccess: () => {
        this.scheduleListData.loadItems().subscribe();
        this.scheduleDetailData.loadItem(this.data.scheduleId);
        this.dialogRef.close({action: 'reverted', versionId: version.id});
      },
      onComplete: () => this.revertingVersionId = null,
    });
  }

  deleteVersion(version: ScheduleVersion) {
    this.versionHelper.deleteWithDialog(version, {
      onStart: () => this.deletingVersionIds.add(version.id),
      onSuccess: () => this.selectedVersionIds.delete(version.id),
      onComplete: () => this.deletingVersionIds.delete(version.id),
    });
  }

  deleteSelectedVersions() {
    const selectedIds = Array.from(this.selectedVersionIds);
    const versions = this.versionListData.peekItems() ?? [];
    const selectedVersions = versions.filter(v => selectedIds.includes(v.id));

    this.versionHelper.bulkDeleteWithDialog(this.data.scheduleId, selectedVersions, {
      onStart: () => selectedIds.forEach(id => this.deletingVersionIds.add(id)),
      onSuccess: () => this.selectedVersionIds.clear(),
      onComplete: () => selectedIds.forEach(id => this.deletingVersionIds.delete(id)),
    });
  }

}
